import api from "@/shared/services/apiClient";
import type { LeaveTemplate, LeaveTemplateConfig, LeaveField, LeaveSubmission, LeaveTemplateSummary } from "@/features/leave/types/leave";

export const leaveService = {
  /** Get all active templates for a company (list view) */
  getTemplates: async (companyId?: string): Promise<LeaveTemplateSummary[]> => {
    const params = companyId ? { company_id: companyId } : {};
    const response = await api.get("/leave/templates", { params });
    return response.data;
  },

  /** Get a specific template by ID, or the company's most recent one */
  getTemplate: async (companyId?: string, templateId?: string): Promise<LeaveTemplate> => {
    const params: Record<string, string> = {};
    if (companyId) params.company_id = companyId;
    if (templateId) params.template_id = templateId;
    const response = await api.get("/leave/template", { params });
    return response.data;
  },

  /** Create a new template (no templateId) or update an existing one (with templateId) */
  saveTemplate: async (
    config: LeaveTemplateConfig,
    fields: LeaveField[],
    companyId?: string,
    title?: string,
    description?: string,
    templateId?: string,
    category?: string,
    isPublished?: boolean,
  ): Promise<LeaveTemplate> => {
    const response = await api.post("/leave/template", {
      config,
      fields,
      ...(companyId ? { company_id: companyId } : {}),
      ...(title !== undefined ? { title } : {}),
      ...(description !== undefined ? { description } : {}),
      ...(templateId ? { template_id: templateId } : {}),
      ...(category !== undefined ? { category } : {}),
      ...(isPublished !== undefined ? { is_published: isPublished } : {}),
    });
    return response.data;
  },

  submit: async (templateId: string, answers: Record<string, string>, targetUserId?: string): Promise<{ message: string }> => {
    const response = await api.post("/leave/submit", {
      templateId,
      answers,
      ...(targetUserId ? { targetUserId } : {}),
    });
    return response.data;
  },

  getSubmissions: async (companyId?: string, startDate?: string, endDate?: string): Promise<LeaveSubmission[]> => {
    const params: Record<string, string> = {};
    if (companyId) params.company_id = companyId;
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    const response = await api.get("/leave/submissions", { params });
    return response.data;
  },

  deleteTemplate: async (templateId: string): Promise<void> => {
    await api.delete(`/leave/template/${templateId}`);
  },

  getMySubmissions: async (): Promise<LeaveSubmission[]> => {
    const response = await api.get("/leave/my-submissions");
    return response.data;
  },
};
